<template>
  <t-dropdown v-clickoutside="onClickOutside" trigger-mode="custom" :visibled="customVisibled">
    <div slot="trigger" @click="onTriggerClick" @mouseenter="addFocusClass" @mouseleave="removeFocusClass">
      <span ref="picker" :class="classes">
        <span :class="prefix+'-picker-label'">{{ showContent }}</span>
        <input v-model="filterValue" :readonly="readonly" type="text" :placeholder="getPlaceholder" :class="inputClasses" autocomplete="off" @input="debouncedInputChange">
        <t-icon v-show="arrowIconVisibled" icon="chevron-down-outline"></t-icon>
        <t-icon v-show="closeIconVisibled" icon="close-circle" class="cursor-pointer" @click.native.stop="clearAll"></t-icon>
      </span>
    </div>
    <div slot="popper">
      <div :class="prefix+'-menus'">
        <multi-menu v-if="!filterInput" :options="options" :expand-trigger="expandTrigger"></multi-menu>
        <div v-else :class="prefix+'-search-list'" :style="popperStyle">
          <div
            v-for="(item, index) of filteredFlatOptions"
            :key="index"
            :value="item.value"
            :class="{[prefix+'-menuitem']:!!item.value, 'no-match': !item.value}"
            @click.stop="selectFilterVal(item)"
            v-html="item.label"
          >
          </div>
        </div>
      </div>
    </div>
  </t-dropdown>
</template>

<script>
import MultiMenu from './menu'
import bus from '../../utils/bus'
import clickoutside from '../../directives/click-outside'
import { findComponentUpward } from '../../utils/assist'
import Emitter from '../../mixins/emitter'
import Locale from '../../mixins/locale'
import { PREFIX_CLASS } from '../../utils/constants'

function debounce(fn, delay) {
  let timer = null
  return function() {
    let context = this
    let args = arguments
    clearTimeout(timer)
    timer = setTimeout(function() {
      fn.apply(context, args)
    }, delay)
  }
}
export default {
  name: 'TCascading',
  components: {
    MultiMenu
  },
  directives: { clickoutside },
  mixins: [Emitter, Locale],
  props: {
    options: {
      type: Array,
      default: null
    },
    value: {
      type: Array,
      default() {
        return []
      }
    },
    changeOnSelect: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loader: {
      type: Function,
      default: () => {}
    },
    filterable: {
      type: Boolean,
      default: false
    },
    debounce: {
      type: Number,
      default: 300
    },
    size: {
      type: String,
      validator(value) {
        return ['lg', '', 'sm'].includes(value)
      },
      default: ''
    },
    separator: {
      type: String,
      default: '/'
    },
    placeholder: {
      type: String,
      default: '请选择'
    },
    clearable: {
      type: Boolean,
      default: false
    },
    expandTrigger: {
      type: String,
      validator(value) {
        return ['click', 'hover'].includes(value)
      },
      default: 'click'
    }
  },

  data() {
    return {
      prefix: `${PREFIX_CLASS}cascading`,
      lastValue: false,
      currentValue: [],
      selected: [],
      customVisibled: false,
      showContent: '',
      filterValue: '',
      flatOptions: [],
      filterInput: false,
      filteredFlatOptions: [],
      readonly: !this.filterable,
      debouncedInputChange: () => {},
      focus: false,
      getPlaceholder: this.placeholder,
      showFocusing: false,
      minWidth: '',
      arrowIconVisibled: true
    }
  },

  computed: {
    closeIconVisibled() {
      return this.focus && this.showContent && this.clearable && this.arrowIconVisibled === false
    },
    popperStyle() {
      return {
        'min-width': this.minWidth
      }
    },
    classes() {
      return [
        `${this.prefix}-picker`,
        {
          [`${this.prefix}-picker-${this.size}`]: this.size,
          [`${this.prefix}-open`]: this.customVisibled
        }
      ]
    },
    inputClasses() {
      return {
        'focus': this.focus,
        'focusing': this.showFocusing
      }
    }
  },

  watch: {
    value: {
      handler(val) {
        this.currentValue = val
        if (!val.length) this.selected = []
      },
      immediate: true
    },
    currentValue(val) {
      this.showContent = this.selected.map(v => v.label).join(` ${this.separator} `)
    },
    customVisibled(val) {
      if (val === false && !this.showContent) {
        this.getPlaceholder = this.placeholder
        this.filterValue = ''
        if (this.filterable) {
          if (this.lastValue) {
            this.getPlaceholder = ''
            this.showContent = this.selected.map(v => v.label).join(` ${this.separator} `)
          } else {
            this.showContent = ''
          }
        }
      }
    },
    showContent(val) {
      if (this.filterable) {
        return
      }
      this.getPlaceholder = val ? '' : this.placeholder
    },
    filterValue(val) {
      if (val) {
        this.showContent = ''
        this.getPlaceholder = ''
      }
    }
  },
  created() {
    bus.$on('updateCurrentValue', ({ fromInit, lastValue, self }) => {
      let selectedTCascader = findComponentUpward(self, 'TCascading')
      if (selectedTCascader === this) {
        if (lastValue || this.changeOnSelect) {
          this.lastValue = lastValue
          this.currentValue = this.selected.map(v => v.value)
          this.getPlaceholder = ''
          if (!fromInit || this.filterable) {
            this.onEmitValue()
          }
        }
        if (lastValue) {
          this.customVisibled = false
        }
      }
    })

    bus.$on('selectedCurrentValue', ({ fromInit, lastValue, self, baseItem, level }) => {
      let selectedTCascader = findComponentUpward(self, 'TCascading')
      if (selectedTCascader === this) {
        if (fromInit) {
          this.selected.push(baseItem)
        } else {
          let index = level - 1
          this.selected[index] = baseItem
          if ((lastValue && this.selected.length > level) || this.changeOnSelect) {
            this.selected = this.selected.slice(0, level)
          }
        }
      }
    })

    this.debouncedInputChange = debounce(event => {
      let value = event.target.value
      this.$nextTick(() => {
        this.handleInputChange(value)
      })
    }, this.debounce)
  },

  mounted() {
    this.minWidth = window.getComputedStyle(this.$refs.picker).getPropertyValue('width')
    this.updateSelected()
    if (this.currentValue && this.currentValue.length > 0) {
      this.showContent = this.currentValue.join(` ${this.separator} `)
    }
    this.flatOptions = this.flattenOptions(this.options)
  },

  methods: {
    clearAll() {
      let menu = this.$children[0].$children[0]
      menu.number = ''
      menu.sublist = []
      this.customVisibled = false
      this.showContent = ''
      this.filterValue = ''
      this.selected = []
      this.currentValue = []
      this.lastValue = false
      this.arrowIconVisibled = true
      this.onEmitValue()
    },
    addFocusClass() {
      if (this.clearable && this.showContent) {
        this.arrowIconVisibled = false
      }
      this.focus = true
    },
    removeFocusClass() {
      if (this.clearable && this.showContent) {
        this.arrowIconVisibled = true
      }
      this.focus = false
    },
    selectFilterVal(item) {
      this.filterValue = ''
      this.filterInput = false
      this.currentValue = item.value
      this.selected = []
      setTimeout(() => {
        this.updateSelected()
      }, 0)
    },
    handleInputChange(value) {
      if (!value) {
        if (this.filterable && !this.showContent) {
          this.getPlaceholder = this.lastValue ? this.selected.map(v => v.label).join(` ${this.separator} `) : this.placeholder
        }
        this.filterInput = false
        return
      }
      let flatOptions = this.flatOptions
      let filteredFlatOptions = flatOptions.filter(optionsStack => {
        return optionsStack.some(option => new RegExp(value, 'i')
          .test(option.label))
      })
      if (filteredFlatOptions.length > 0) {
        filteredFlatOptions = filteredFlatOptions.map(optionStack => {
          return {
            value: optionStack.map(option => option.value),
            label: this.renderFilteredOptionLabel(value, optionStack).join(` ${this.separator} `),
            disabled: optionStack.some(item => item.disabled)
          }
        })
      } else {
        filteredFlatOptions = [{
          label: this.t('t.select.noMatch'),
          value: '',
          disabled: true
        }]
      }
      this.filterInput = true
      this.filteredFlatOptions = filteredFlatOptions
      this.customVisibled = true
    },
    renderFilteredOptionLabel(inputValue, optionsStack) {
      return optionsStack.map((option, index) => {
        const label = option.label
        const keywordIndex = label.toLowerCase().indexOf(inputValue.toLowerCase())
        const labelPart = label.slice(keywordIndex, inputValue.length + keywordIndex)
        return keywordIndex > -1 ? this.highlightKeyword(label, labelPart) : label
      })
    },
    highlightKeyword(label, keyword) {
      return label.split(keyword)
        .map((node, index) => index === 0 ? node : `<span style="color:#0b69ff">${keyword}</span>${node}`).join('')
    },
    flattenOptions(options, ancestor = []) {
      let flatOptions = []
      options.forEach((option) => {
        const optionsStack = ancestor.concat(option)
        if (!option.children) {
          flatOptions.push(optionsStack)
        } else {
          if (this.changeOnSelect) {
            flatOptions.push(optionsStack)
          }
          flatOptions = flatOptions.concat(this.flattenOptions(option.children, optionsStack))
        }
      })
      return flatOptions
    },
    onEmitValue() {
      this.$emit(
        'input',
        this.currentValue
      )
      this.$emit(
        'onChange',
        this.currentValue,
        JSON.parse(JSON.stringify(this.selected))
      )
    },
    onTriggerClick(res) {
      this.customVisibled = !this.customVisibled
      if (!this.customVisibled) return
      this.showFocusing = true
      this.getPlaceholder = ''
      if (this.filterable && this.showContent) {
        this.getPlaceholder = this.showContent
        this.showContent = ''
      }
      if (!this.lastValue && !this.changeOnSelect) {
        let menu = this.$children[0].$children[0]
        if (menu) {
          menu.number = ''
          menu.sublist = []
          return
        }
      }
      if (this.$children && this.selected.length > 0) {
        let $children = this.findAllMenuComponents(this.$children)
        $children.forEach((child, i) => {
          for (let j = 0; j < child.options.length; j++) {
            if (this.selected[i]) {
              if (child.options[j].value === this.selected[i].value) {
                child.number = j
                break
              }
            }
          }
        })
      }
    },
    findAllMenuComponents($children, arr = []) {
      $children.forEach(child => {
        if (child.$options._componentTag === 'multi-menu') {
          arr.push(child)
        }
        if (child.$children) {
          this.findAllMenuComponents(child.$children, arr)
        }
      })
      return arr
    },
    onClickOutside() {
      this.showFocusing = false
      this.customVisibled = false
      this.filterInput = false
    },
    updateSelected() {
      this.broadcast('MultiMenu', 'on-find-selected', {
        value: this.currentValue
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/module/new-cascader/main.vue